import GpsTrack from './GpsTrack';
import L from 'leaflet';
import Segment from './Segment';
import { TrackStatus } from './types';
import { distanceBetweenPoints } from './utils/coords';
import { getCenter } from 'geolib';
import * as icons from '@/ts/icons';
import axios from 'axios';
import type { LatLng } from 'Leaflet.MultiOptionsPolyline';

export default class Track {

  public id: number;
  public maximized: boolean;
  public lastRefresh: number;
  public markerIcon: L.Icon;
  public middleMarkerIcon: L.Icon;
  public lastDistance: number;

  public elevationRequestId: number;

  public lastClickedPointerType: string;

  constructor(public gpsTrack: GpsTrack, public checked: boolean, public onServer: boolean) {
    this.id = gpsTrack.id;
    this.lastRefresh = 0;
    this.maximized = false;
    this.lastDistance = 0;

    this.markerIcon = L.icon({
      iconUrl: icons.redCircle,
      iconSize: [13, 13],
      iconAnchor: [6, 6],
    });

    this.middleMarkerIcon = L.icon({
      iconUrl: icons.blueCircle,
      iconSize: [9, 9],
      iconAnchor: [4, 4],
    });
  }

  public dragEnd(event: L.DragEndEvent, params: {'obj': Track, 'point': L.LatLng, 'map': L.Map}) {
    const point = params.point;
    point.lat = (event.target.getLatLng()).lat;
    point.lng = (event.target.getLatLng()).lng;
    const documentObject = window.tracks[this.id];
    documentObject.mapTracks[0].setLatLngs(params.obj.gpsTrack.segments[0].pointsArray);
    if (point.nextPoint) {
      const middle = getCenter([{latitude: point.lat, longitude: point.lng}, {latitude: point.nextPoint.lat, longitude: point.nextPoint.lng}]);
      point.nextPoint.middleMarker.setLatLng([middle.latitude, middle.longitude]);
    }
    if (point.prevPoint) {
      const middle = getCenter([{latitude: point.lat, longitude: point.lng}, {latitude: point.prevPoint.lat, longitude: point.prevPoint.lng}]);
      point.middleMarker.setLatLng([middle.latitude, middle.longitude]);
    }
    // update tooltips and distances for the whole track
    this.updateDistancesAndTooltips();
    try {
      params.obj.requestElevationGain();
    } catch (e) {
      // ignore
    }
  }

  // Usuwanie markera czerwonego
  public clickPlannedMarker(event: L.LeafletMouseEvent, params: {'obj': Track, 'point': L.LatLng, 'map': L.Map}) {
    let removeMarker = false;
    if (event.type == 'click') {
      this.lastClickedPointerType = event.originalEvent.pointerType;
    }
    if ((event.type == 'click') && ((event.originalEvent.ctrlKey || event.originalEvent.metaKey || event.originalEvent.shiftKey))) {
      removeMarker = true;
    }
    if ((event.type == 'dblclick') && (this.lastClickedPointerType == 'pen')) {
      removeMarker = true;
    }
    if (removeMarker) {
      const documentObject = window.tracks[this.id];
      const pointsArray = params.obj.gpsTrack.segments[0].pointsArray;
      const index = pointsArray.indexOf(params.point);
      if (index >= 0) {
        const point = pointsArray[index];
        const clickedMarker = this.getEventTarget(event);
        const plannedMarkerIndexOf = this.findMarkerIndex(documentObject.plannedMarkers, point.plannedMarker, clickedMarker);
        if (plannedMarkerIndexOf >= 0) {
          const markerToRemove = documentObject.plannedMarkers[plannedMarkerIndexOf];
          markerToRemove.removeFrom(window.mainmap);
          documentObject.plannedMarkers.splice(plannedMarkerIndexOf, 1);
        }
        if (point.middleMarker) {
          const middleMarkerIndexOf = this.findMarkerIndex(documentObject.middleMarkers, point.middleMarker);
          point.middleMarker.removeFrom(window.mainmap);
          if (middleMarkerIndexOf >= 0) {
            documentObject.middleMarkers.splice(middleMarkerIndexOf, 1);
          }
        }
        if (point.prevPoint) {
          point.prevPoint.nextPoint = point.nextPoint;
        } else {
          if (point.nextPoint) {
            documentObject.startMarker.setLatLng(point.nextPoint);
          } else {
            documentObject.startMarker.removeFrom(window.mainmap);
          }
        }
        if (point.nextPoint) {
          point.nextPoint.prevPoint = point.prevPoint;
          if (point.prevPoint) {
            const middle = getCenter([{latitude: point.prevPoint.lat, longitude: point.prevPoint.lng}, {latitude: point.nextPoint.lat, longitude: point.nextPoint.lng}]);
            point.nextPoint.middleMarker.setLatLng([middle.latitude, middle.longitude]);
          } else {
            // removing first point: also remove the middle marker that belonged to the next point
            if (point.nextPoint.middleMarker) {
              const midIdx = this.findMarkerIndex(documentObject.middleMarkers, point.nextPoint.middleMarker);
              point.nextPoint.middleMarker.removeFrom(window.mainmap);
              if (midIdx >= 0) { 
                documentObject.middleMarkers.splice(midIdx, 1); 
              }
              point.nextPoint.middleMarker = null;
            }
          }
        } else {
          if (point.prevPoint) {
            documentObject.finishMarker.setLatLng(point.prevPoint);
          } else {
            documentObject.finishMarker.removeFrom(window.mainmap);
          }
        }
        pointsArray.splice(index, 1);
        documentObject.mapTracks[0].setLatLngs(pointsArray);
      }
      // update tooltips and distances for the whole track
      this.updateDistancesAndTooltips();
      try {
        params.obj.requestElevationGain();
      } catch (e) {
        // ignore
      }
    }
  }

  public clickMiddleMarker(event: L.LeafletMouseEvent, params: {'obj': Track, 'point': L.LatLng, 'map': L.Map}) {
    const pointsArray = params.obj.gpsTrack.segments[0].pointsArray;
    const index = pointsArray.indexOf(params.point);
    if (index >= 0) {
      const documentObject = window.tracks[this.id];
      const point = params.point;
      const newPoint = event.latlng;
      newPoint.nextPoint = point;
      newPoint.prevPoint = point.prevPoint;
      point.prevPoint.nextPoint = newPoint;
      point.prevPoint = newPoint;
      const newPlannedMarker = this.createPlannedMarker(newPoint);
      newPlannedMarker.bindTooltip('', {permanent: true, className: 'smallTooltip', direction: 'right'});
      newPoint.plannedMarker = newPlannedMarker;
      const newMiddleMarker = this.createMiddleMarker(newPoint, newPoint.prevPoint);
      newPoint.middleMarker = newMiddleMarker;
      const middle = getCenter([{latitude: point.lat, longitude: point.lng}, {latitude: newPoint.lat, longitude: newPoint.lng}]);
      point.middleMarker.setLatLng([middle.latitude, middle.longitude]);
      pointsArray.splice(index, 0, event.latlng);
      newPlannedMarker.addTo(window.mainmap);
      newMiddleMarker.addTo(window.mainmap);
      documentObject.mapTracks[0].setLatLngs(pointsArray);
      documentObject.plannedMarkers.push(newPlannedMarker);
      documentObject.middleMarkers.push(newMiddleMarker);
      // update tooltips and distances for the whole track
      this.updateDistancesAndTooltips();
    }
    try {
      params.obj.requestElevationGain();
    } catch (e) {
      // ignore
    }
    L.DomEvent.stopPropagation(event);
    return false;
  }

  public async requestElevationGain(): Promise<void> {
    this.gpsTrack.elevation_gain = 0;
    // Only for bicycle planned tracks (skip borders)
    try {
      if (!this.gpsTrack || !this.gpsTrack.isBicycleTrack() || this.gpsTrack.isBorderTrack() || !this.gpsTrack.isPlannedTrack()) {
        return;
      }

      // Build segments array [[ [lat, lon], ... ], ...]
      const segments: any[] = [];
      for (const segment of this.gpsTrack.segments) {
        const pts: any[] = [];
        for (const p of segment.pointsArray) {
          pts.push([p.lat, p.lng]);
        }
        segments.push(pts);
      }

      const requestId = Date.now();
      this.elevationRequestId = requestId;
      const obj = { request_id: requestId, data: segments };
      // clear current elevation on frontend to indicate recalculation
      try { this.gpsTrack.elevation_gain = 0; } catch (e) {}
      const resp = await axios.post((window as any).apiHost + `api/elevationgain/`, obj);
      const json = resp && resp.data ? resp.data : null;
      if (!json) return;
      if (json.request_id !== this.elevationRequestId) {
        // stale response
        return;
      }
      if (json.elevation_gain === null || typeof json.elevation_gain === 'undefined') {
        this.gpsTrack.elevation_gain = null;
      } else {
        this.gpsTrack.elevation_gain = json.elevation_gain;
      }
    } catch (e) {
      // ignore errors
    }
  }

  private createPlannedMarker(point: LatLng) {
    const marker = new L.Marker(point, {draggable: true, icon: this.markerIcon, zIndexOffset: 100000, contextmenu: false, contextmenuItems: []});
    const dragParams = {'obj': this, 'point': point, 'map': window.mainmap};
    marker.on('dragend', (e) => this.dragEnd(e, dragParams));
    marker.on('click', (e) => this.clickPlannedMarker(e, dragParams));
    marker.on('dblclick', (e) => this.clickPlannedMarker(e, dragParams));
    return marker;
  }

  private createMiddleMarker(point: LatLng, lastPoint: LatLng) {
    const middle = getCenter([{latitude: point.lat, longitude: point.lng}, {latitude: lastPoint.lat, longitude: lastPoint.lng}]);
    if (middle) {
      const dragParams = {'obj': this, 'point': point, 'map': window.mainmap};
      const middleMarker = new L.Marker([middle.latitude, middle.longitude], {icon: this.middleMarkerIcon, zIndexOffset: 50000, contextmenu: false, contextmenuItems: []});
      middleMarker.on('click', (e) => this.clickMiddleMarker(e, dragParams));
      return middleMarker;
    }
  }

  private getEventTarget(event: any): any {
    try { return event && (event.target || (event as any).target) || null; } catch (e) { return null; }
  }

  private findMarkerIndex(list: any[], marker: any, eventTarget?: any): number {
    let idx = -1;
    if (eventTarget) {
      idx = list.indexOf(eventTarget);
      if (idx !== -1) return idx;
    }
    if (marker) {
      idx = list.indexOf(marker);
      if (idx !== -1) return idx;
    }
    const targetId = (eventTarget && (eventTarget as any)._leaflet_id !== undefined) ? (eventTarget as any)._leaflet_id : null;
    if (targetId !== null) {
      for (let i = 0; i < list.length; i++) {
        const m: any = list[i];
        if (m && (m._leaflet_id !== undefined) && (m._leaflet_id === targetId)) return i;
      }
    }
    const markerId = (marker && (marker as any)._leaflet_id !== undefined) ? (marker as any)._leaflet_id : null;
    if (markerId !== null) {
      for (let i = 0; i < list.length; i++) {
        const m: any = list[i];
        if (m && (m._leaflet_id !== undefined) && (m._leaflet_id === markerId)) return i;
      }
    }
    return -1;
  }

  private updateDistancesAndTooltips() {
    try {
      const segments = this.gpsTrack && this.gpsTrack.segments ? this.gpsTrack.segments : [];
      let lastPoint: L.LatLng = null;
      let cumulative = 0;
      let total = 0;
      for (const seg of segments) {
        try { if (seg && typeof seg.calculateDistance === 'function') seg.calculateDistance(); } catch (e) {}
      }
      for (const segment of segments) {
        for (const point of segment.pointsArray) {
          if (lastPoint) {
            cumulative += distanceBetweenPoints(point.lat, point.lng, lastPoint.lat, lastPoint.lng);
          }
          const distanceLabel = this.getDistanceLabelForMarker(cumulative);
          try {
            if (point.plannedMarker && (point.plannedMarker as L.Marker).getTooltip) {
              (point.plannedMarker as L.Marker).getTooltip().setContent(distanceLabel);
            }
          } catch (e) {}
          lastPoint = point;
        }
        if (segment && typeof segment.distance === 'number') total += segment.distance;
      }
      this.lastDistance = cumulative;
      this.gpsTrack.distance = total;
    } catch (e) {
      // ignore errors during recalculation
    }
  }

  public getDistanceLabelForMarker(distance: number) {
    let decimalPlaces = 1;
    if ((distance > 0) && (distance < 2500)) {
      decimalPlaces = 3;
    }
    return (distance / 1000).toFixed(decimalPlaces);
  }

  public createMapObjects(map: L.Map) {
    window.tracks[this.id] = {mapTracks: [], animateTracks: [], plannedMarkers: [], middleMarkers: [], startMarker: null, finishMarker: null};
    const documentObject = window.tracks[this.id];
    for (const segment of this.gpsTrack.segments) {
      const poliline = new L.Polyline(segment.pointsArray, {
        color: this.gpsTrack.color,
        weight: 3,
        opacity: 1,
        smoothFactor: 1,
        dashArray: this.convertStyleIDToDash(this.gpsTrack.style),
      });
      documentObject.mapTracks.push(poliline);
    }
    let startIcon = null;
    let finishIcon = null;
    if (! this.gpsTrack.isBorderTrack()) {
      startIcon = L.icon({
        iconUrl: icons.markerTrackStartIcon,
        iconSize: [21, 21],
        iconAnchor: [10, 10],
      });
      finishIcon = L.icon({
        iconUrl: icons.markerTrackFinishIcon,
        iconSize: [21, 21],
        iconAnchor: [10, 10],
      });
    } else {
      startIcon = L.icon({
        iconUrl: icons.markerTrackStartIcon,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      finishIcon = L.icon({
        iconUrl: icons.markerTrackFinishIcon,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      }); 
    }
    const animateTrackHtml = this.gpsTrack.isBicycleTrack() ? '<img style="height: 20px;" src="' + icons.bicycle + '" />' : '<img style="height: 20px;" src="' + icons.shoe + '" />';
    for (const segment of this.gpsTrack.segments) {
      // @ts-ignore
      const animateTrack = L.motion.polyline(segment.pointsArray, {
        weight: 3,
        opacity: 1,
        smoothFactor: 1,
      }, {
        auto: false,
        // @ts-ignore
        easing: L.Motion.Ease.linear,
      }, {removeOnEnd: true, icon: L.divIcon({
        html: animateTrackHtml,
        iconSize: [32, 32],
        className: 'animateTrackIcon',
      })});
      documentObject.animateTracks.push(animateTrack);
    }
    if (this.gpsTrack.segments.length) {
      const firstSegment = this.gpsTrack.segments[0].pointsArray;
      const lastSegment = this.gpsTrack.segments[this.gpsTrack.segments.length - 1].pointsArray;
      documentObject.startMarker = new L.Marker(firstSegment[0], {icon: startIcon, zIndexOffset: 1, contextmenu: false, contextmenuItems: []});
      documentObject.finishMarker = new L.Marker([lastSegment[lastSegment.length - 1].lat, lastSegment[lastSegment.length - 1].lng], {icon: finishIcon, zIndexOffset: 2, contextmenu: false, contextmenuItems: []});
      if ((this.gpsTrack.status === TrackStatus.planned) || (this.gpsTrack.status === TrackStatus.border)) {
        let lastPoint: L.LatLng = null;
        let distance = 0;
        for (const segment of this.gpsTrack.segments) {
          for (const point of segment.pointsArray) {
            const marker = this.createPlannedMarker(point)
            point.plannedMarker = marker;
            if (lastPoint) {
              lastPoint.nextPoint = point;
              point.prevPoint = lastPoint;
              distance = distance + distanceBetweenPoints(point.lat, point.lng, lastPoint.lat, lastPoint.lng);
              const middleMarker = this.createMiddleMarker(point, lastPoint);
              if (middleMarker) {  
                documentObject.middleMarkers.push(middleMarker);
                point.middleMarker = middleMarker;
                lastPoint = point;
              }
            } else {
              lastPoint = point;
            }
            const distanceLabel = this.getDistanceLabelForMarker(distance)
            marker.bindTooltip(distanceLabel, {permanent: true, className: 'smallTooltip', direction: 'right'});
            this.lastDistance = distance;
            documentObject.plannedMarkers.push(marker);
          }
        }
      }
    }
  }

  public convertStyleIDToDash(style: number) {
    if (style == 1) {
      return [1000, 0];
    }
    if (style == 2) {
      return [9, 6];
    }
    if (style == 3) {
      return [9, 10];
    }
    if (style == 4) {
      return [6, 5];
    }
    if (style == 5) {
      return [6, 7];
    }
    if (style == 6) {
      return [1, 5];
    }
    if (style == 7) {
      return [1, 8];
    }
    return [1000, 0];
  }

  public removeMapObjects(map: L.Map) {
    const documentObject = window.tracks[this.id];
    for (const mapTrack of documentObject.mapTracks) {
      mapTrack.removeFrom(map);
    }
    for (const animateTrack of documentObject.animateTracks) {
      animateTrack.removeFrom(map);
    }
    for (const marker of documentObject.plannedMarkers) {
      marker.removeFrom(map);
    }
    for (const marker of documentObject.middleMarkers) {
      marker.removeFrom(map);
    }
    if (documentObject.startMarker) {
      documentObject.startMarker.removeFrom(map);
    }
    if (documentObject.finishMarker) {
      documentObject.finishMarker.removeFrom(map);
    }
  }

  public refreshMapObjects(map: L.Map) {
    this.removeMapObjects(map);
    this.createMapObjects(map);
  }

  public addPoint(lat: number, lon: number, map: L.Map) {
    if (this.gpsTrack.segments.length === 0) {
      const segment = new Segment([[lat, lon]]);
      this.gpsTrack.segments.push(segment);
      this.createMapObjects(window.mainmap);
      this.lastRefresh = Date.now();
    } else {
      this.gpsTrack.segments[0].addPoint(lat, lon);
      const newPoint = this.gpsTrack.segments[0].pointsArray.at(-1);
      const newPlannedMarker = this.createPlannedMarker(newPoint);
      newPlannedMarker.bindTooltip('', {permanent: true, className: 'smallTooltip', direction: 'right'});
      const documentObject = window.tracks[this.id];
      newPoint.plannedMarker = newPlannedMarker;
      documentObject.plannedMarkers.push(newPlannedMarker);
      newPlannedMarker.addTo(window.mainmap);
      documentObject.mapTracks[0].addLatLng(newPoint);
      documentObject.finishMarker.setLatLng(newPoint);
      const lastPoint = this.gpsTrack.segments[0].pointsArray.at(-2);
      if (lastPoint) {
        lastPoint.nextPoint = newPoint;
        const newMiddleMarker = this.createMiddleMarker(newPoint, lastPoint);
        documentObject.middleMarkers.push(newMiddleMarker);
        newMiddleMarker.addTo(window.mainmap);
        newPoint.middleMarker = newMiddleMarker;
      }
      newPoint.prevPoint = lastPoint;
      // recompute distances/tooltips and track distance after adding point
      documentObject.mapTracks[0].setLatLngs(this.gpsTrack.segments[0].pointsArray);
      this.updateDistancesAndTooltips();
    }
    try {
      this.requestElevationGain();
    } catch (e) {
      // ignore
    }
  }

}
